import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PackageSearch, Loader2, Check, XCircle, Truck, Store, Phone } from 'lucide-react';
import { trackOrder } from '../../services/orders.service';
import { TrackedOrder } from '../../types';

const OPEN_EVENT = 'ndms:open-track';

/** Open the Track Order modal from anywhere (navbar, footer, success screen). */
export function openTrackOrder() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

const STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'preparing', label: 'Being prepared' },
  { key: 'ready', label: 'Ready' },
  { key: 'completed', label: 'Picked up' },
] as const;

export function TrackOrderModal() {
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<TrackedOrder | null>(null);

  // Open on demand from navbar / footer
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Prefill from the ?track= link in the confirmation email, or the last order placed here
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const track = url.searchParams.get('track');
      const lastRaw = localStorage.getItem('ndms-last-order');
      const last = lastRaw ? JSON.parse(lastRaw) : null;
      if (track) {
        const id = track.toUpperCase();
        setOrderId(id);
        if (last && last.orderId === id && typeof last.email === 'string') setEmail(last.email);
        setOpen(true);
        url.searchParams.delete('track');
        window.history.replaceState({}, '', url.toString());
      } else if (last && typeof last.orderId === 'string') {
        setOrderId(last.orderId);
        if (typeof last.email === 'string') setEmail(last.email);
      }
    } catch {
      // bad localStorage / URL — start blank
    }
  }, []);

  // Esc closes; lock scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleLookup = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!orderId.trim() || !email.trim()) {
      setError('Enter both your order ID and the email you used.');
      return;
    }
    setLoading(true);
    setError(null);
    setOrder(null);
    try {
      const data = await trackOrder(orderId.trim().toUpperCase(), email.trim());
      setOrder(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) setError("We couldn't find that order. Double-check the order ID and the email you used when ordering.");
      else if (status === 429) setError('Too many lookups — please wait a few minutes and try again.');
      else setError('Something went wrong. Please try again, or call us at 306-979-4115.');
    } finally {
      setLoading(false);
    }
  }, [orderId, email]);

  const isDelivery = order?.fulfillmentMethod === 'delivery';
  const currentStep = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto scrollbar-thin shadow-2xl"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-ndms-navy flex items-center justify-center">
                  <PackageSearch size={17} className="text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-ndms-navy text-base leading-tight">Track Your Order</h2>
                  <p className="text-[11px] text-gray-400 leading-tight">Order ID + the email you used</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              {/* Lookup form */}
              <form onSubmit={handleLookup} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-ndms-navy mb-1.5">Order ID</label>
                  <input
                    type="text"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value.toUpperCase())}
                    placeholder="ORD-20260715-A1B2C3"
                    maxLength={24}
                    className="input-base font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ndms-navy mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    maxLength={254}
                    className="input-base text-sm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-ndms-navy text-white font-bold rounded-2xl hover:bg-ndms-navy-light transition-all disabled:opacity-60"
                >
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> Looking up…</>) : 'Check Status'}
                </button>
              </form>

              {error && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-3 text-xs text-amber-800 leading-snug">
                  {error}
                </div>
              )}

              {/* Result */}
              {order && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  {order.status === 'cancelled' ? (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
                      <XCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-red-800">This order was cancelled</p>
                        <p className="text-xs text-red-600 mt-0.5">
                          Unexpected? Call us at <a href="tel:+13069794115" className="underline font-semibold">306-979-4115</a>.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-ndms-cream rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        {isDelivery ? <Truck size={15} className="text-ndms-blue" /> : <Store size={15} className="text-ndms-blue" />}
                        <p className="text-xs font-semibold text-ndms-navy">
                          {isDelivery ? 'Delivery order' : 'Pickup order'} · <span className="font-mono">{order.orderId}</span>
                        </p>
                      </div>
                      <div className="space-y-0">
                        {STEPS.map((step, i) => {
                          const done = i <= currentStep;
                          const isCurrent = i === currentStep;
                          const label =
                            step.key === 'completed' ? (isDelivery ? 'Delivered' : 'Picked up')
                            : step.key === 'ready' ? (isDelivery ? 'Ready — arranging delivery' : 'Ready for pickup')
                            : step.label;
                          return (
                            <div key={step.key} className="flex items-start gap-3">
                              <div className="flex flex-col items-center">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                                  done ? 'bg-ndms-navy border-ndms-navy' : 'bg-white border-gray-300'
                                }`}>
                                  {done && <Check size={13} className="text-white" />}
                                </div>
                                {i < STEPS.length - 1 && (
                                  <div className={`w-0.5 h-6 ${i < currentStep ? 'bg-ndms-navy' : 'bg-gray-200'}`} />
                                )}
                              </div>
                              <p className={`text-sm pt-0.5 ${
                                isCurrent ? 'font-bold text-ndms-navy' : done ? 'font-medium text-gray-600' : 'text-gray-400'
                              }`}>
                                {label}
                                {isCurrent && step.key === 'ready' && !isDelivery && (
                                  <span className="block text-[11px] font-normal text-green-700 mt-0.5">
                                    Come by whenever suits you — payment in store.
                                  </span>
                                )}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Items */}
                  <div>
                    <p className="text-xs font-semibold text-ndms-navy mb-2">Items ({order.items.length})</p>
                    <ul className="space-y-1.5">
                      {order.items.map((item, i) => (
                        <li key={i} className="text-xs text-gray-600 flex justify-between gap-3">
                          <span className="min-w-0 truncate">{item.brand} – {item.productName}{item.variantDescription ? ` (${item.variantDescription})` : ''}</span>
                          <span className="font-semibold flex-shrink-0">×{item.quantity}</span>
                        </li>
                      ))}
                    </ul>
                    {order.createdAt && (
                      <p className="text-[11px] text-gray-400 mt-2">
                        Placed {new Date(order.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-gray-400 border-t border-gray-100 pt-3">
                    <Phone size={12} />
                    Questions? <a href="tel:+13069794115" className="text-ndms-blue font-semibold hover:underline">306-979-4115</a>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
